import React, { useState, useEffect, useCallback } from 'react';
import { HiLink, HiCheck, HiClipboardDocument } from 'react-icons/hi2';
import { HiX } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';

export default function ShareModal({ open, doc, onClose, onCopy }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (doc?.url) {
      navigator.clipboard.writeText(doc.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      setTimeout(() => onCopy(), 1000);
    }
  }, [doc?.url, onCopy]);

  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') onClose();
      if ((e.metaKey || e.ctrlKey) && e.key === 'c') {
        e.preventDefault();
        handleCopy();
      }
    },
    [onClose, handleCopy]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  return (
    <AnimatePresence>
      {open && doc && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
          >
            {/* Header */}
            <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white px-6 py-5">
              <div className="flex items-center gap-4">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', delay: 0.1 }}
                  className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-500/40"
                >
                  <HiLink className="h-6 w-6" />
                </motion.div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-black">Share Document</h3>
                  <p className="mt-1 text-sm font-medium text-gray-600">
                    Copy link to share with others
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 transition-all hover:bg-gray-50 hover:text-gray-700 active:scale-95"
                  aria-label="Close"
                >
                  <HiX className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-6">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <label className="mb-3 block text-sm font-semibold text-black">Document Link</label>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <input
                      readOnly
                      value={doc?.url || ''}
                      onClick={e => e.target.select()}
                      className="w-full rounded-lg border-2 border-gray-300 bg-gray-50 px-4 py-3 pr-10 font-mono text-xs font-medium text-black shadow-sm transition-all hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/20"
                    />
                    <HiClipboardDocument className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleCopy}
                    className={`inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all ${
                      copied
                        ? 'bg-green-600 shadow-green-500/30 hover:bg-green-700'
                        : 'bg-blue-600 shadow-blue-500/30 hover:bg-blue-700'
                    }`}
                  >
                    <AnimatePresence mode="wait">
                      {copied ? (
                        <motion.div
                          key="check"
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          exit={{ scale: 0 }}
                          className="flex items-center gap-2"
                        >
                          <HiCheck className="h-4 w-4" />
                          Copied!
                        </motion.div>
                      ) : (
                        <motion.div
                          key="copy"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                          className="flex items-center gap-2"
                        >
                          <HiLink className="h-4 w-4" />
                          Copy
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Click the input to select all or press{' '}
                  <kbd className="rounded bg-gray-200 px-1.5 py-0.5 font-mono">⌘C</kbd> to copy
                </p>
              </motion.div>
            </div>

            {/* Document info */}
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-black">{doc?.fileName}</p>
                  <p className="text-xs text-gray-500">{doc?.category}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
